/**
 * SectionNav — the settings page's grouped section list with its search. The
 * same list is the sidebar on a computer, the sheet behind the section picker
 * on a tablet, and the first screen on a phone. Searching lists matching
 * sections and tabs; choosing a tab opens its section on that tab.
 *
 * Design system: rows in `body` (`value` on a phone), the open one bold on a
 * `brandSubtle` ground with a `brand` edge; group headings in the `label`
 * role over a `borderSubtle` rule; the search is the ds TextInput.
 */
import React, { useState } from 'react';
import { textRole, tokenVars } from '@turn-connect/ui';
import { TextInput } from '../../ui/ds';
import type { SettingsSectionGroup } from './SettingsPageFrame';
import { searchSections } from './sectionSearch';

export interface SectionNavProps {
  groups: SettingsSectionGroup[];
  activeSection: string;
  dirtyIds: string[];
  onChoose: (sectionId: string, tabId?: string) => void;
  /** Phone list: large rows with a chevron. */
  touch?: boolean;
  /** Focus the search when shown (the tablet sheet). */
  autoFocusSearch?: boolean;
}

export const SectionNav: React.FC<SectionNavProps> = ({
  groups,
  activeSection,
  dirtyIds,
  onChoose,
  touch = false,
  autoFocusSearch = false,
}) => {
  const [query, setQuery] = useState('');
  const hits = searchSections(groups, query);
  const searching = query.trim() !== '';

  const row = (
    key: string,
    label: React.ReactNode,
    onClick: () => void,
    opts: { current?: boolean; dirty?: boolean; badge?: React.ReactNode; testId?: string; hint?: string; indent?: boolean } = {}
  ) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-current={opts.current ? 'page' : undefined}
      // The section keeps its own name; its status line and "Unsaved changes"
      // are descriptions, so "Pricing" is still called "Pricing".
      aria-label={opts.hint && typeof label === 'string' ? label : undefined}
      aria-description={[opts.hint, opts.dirty ? 'Unsaved changes' : undefined].filter(Boolean).join('. ') || undefined}
      data-dirty={opts.dirty ? 'true' : undefined}
      data-testid={opts.testId}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        ...textRole(touch ? 'value' : 'body', opts.current ? 'primary' : 'secondary'),
        // Sections are plain weight; only the open one is bold, so headings stay distinct.
        fontWeight: opts.current && !touch ? 600 : touch ? textRole('value').fontWeight : 400,
        background: !touch && opts.current ? tokenVars.brandSubtle : 'none',
        boxShadow: !touch && opts.current ? `inset 3px 0 0 ${tokenVars.brand}` : 'none',
        border: 0,
        borderBottom: touch ? `1px solid ${tokenVars.borderSubtle}` : 0,
        borderRadius: touch ? 0 : 6,
        fontFamily: 'inherit',
        textAlign: 'left',
        padding: touch ? `10px 14px 10px ${opts.indent ? 22 : 14}px` : `6px 8px 6px ${opts.indent ? 16 : 8}px`,
        minHeight: touch ? 48 : 32,
        cursor: 'pointer',
      }}
    >
      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {label}
        {opts.hint && <span style={{ ...textRole('meta'), display: 'block' }}>{opts.hint}</span>}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flex: 'none' }}>
        {opts.badge}
        {opts.dirty && (
          <span
            aria-hidden
            data-testid={opts.testId ? opts.testId.replace('settings-section-', 'settings-section-dirty-') : undefined}
            title="Unsaved changes"
            style={{ width: 7, height: 7, borderRadius: '50%', background: tokenVars.textAccent }}
          />
        )}
        {touch && (
          <span aria-hidden style={{ color: tokenVars.text.muted }}>
            ›
          </span>
        )}
      </span>
    </button>
  );

  return (
    <nav
      aria-label="Settings sections"
      data-testid="settings-section-list"
      style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}
    >
      <TextInput
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search settings"
        aria-label="Search settings"
        data-testid="settings-section-search"
        // eslint-disable-next-line jsx-a11y/no-autofocus -- the sheet opens for searching and choosing
        autoFocus={autoFocusSearch}
        style={{ margin: touch ? '0 12px' : 0, width: touch ? 'calc(100% - 24px)' : '100%' }}
      />
      {searching ? (
        hits.length === 0 ? (
          <p style={{ ...textRole('meta'), margin: touch ? '0 12px' : '0 8px' }}>No settings match “{query.trim()}”.</p>
        ) : (
          <div role="group" aria-label="Search results" style={{ display: 'grid', gap: 1 }}>
            {hits.map((h) =>
              row(
                `${h.section.id}/${h.tab?.id ?? ''}`,
                h.tab ? h.tab.label : h.section.label,
                () => {
                  setQuery('');
                  onChoose(h.section.id, h.tab?.id);
                },
                {
                  hint: h.tab ? h.section.label : undefined,
                  testId: h.tab ? `settings-search-result-${h.section.id}-${h.tab.id}` : `settings-search-result-${h.section.id}`,
                }
              )
            )}
          </div>
        )
      ) : (
        groups.map((group) => (
          <div key={group.label || 'top'} role="group" aria-label={group.label || 'Overview'} style={{ display: 'grid', gap: 1 }}>
            {group.label && (
              // Group headings read as headings, not as one more item: a rule
              // above, brighter bold small caps, and (on a phone) a band of
              // their own; the sections under them are indented.
              <div
                data-testid={`settings-group-${group.label}`}
                style={{
                  margin: touch ? '6px 0 0' : '4px 0 4px',
                  padding: touch ? '8px 14px 6px' : '10px 8px 0',
                  ...textRole('label', 'secondary'),
                  borderTop: touch ? 'none' : `1px solid ${tokenVars.borderSubtle}`,
                  background: touch ? tokenVars.band : 'none',
                }}
              >
                {group.label}
              </div>
            )}
            {group.items.map((item) =>
              row(item.id, item.label, () => onChoose(item.id), {
                indent: !!group.label,
                current: item.id === activeSection,
                dirty: dirtyIds.includes(item.id),
                badge: item.badge,
                testId: `settings-section-${item.id}`,
                // The phone list says where each section stands; the sidebar stays a plain list.
                hint: touch ? item.status : undefined,
              })
            )}
          </div>
        ))
      )}
    </nav>
  );
};

export default SectionNav;
